import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@cbhq/cds-web/buttons';
import { Box, HStack } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { PageFooter } from '@cbhq/cds-web/page/PageFooter';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { useRefreshWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { AppExperiments, useExperiments, useIsTreatment } from '../../../utils/experiments';
import { useManagePermissionsNow } from '../hooks/useManagePermissionsNow';
import { useTeamApi } from '../hooks/useTeamApi';
import { useWorkspaceTeam } from '../hooks/useWorkspaceTeam';
import { getCancelInviteErrorMessage } from '../utils/cancelInviteErrors';
import { getPendingInviteRoute, getTeamMemberRoute, ROUTE_TEAM } from '../utils/constants';
import type { PendingInviteWithGracePeriod } from '../utils/gracePeriod';
import { getManageablePendingInviteEntityIds } from '../utils/pendingInvites';
import { toEmailKey } from '../utils/workspaceTeam';

import { PendingInviteTray } from './PendingInviteTray';

const messages = defineMessages({
  cancelInvite: {
    id: 'Team.pendingInviteDetail.cancelInvite',
    defaultMessage: 'Cancel invite',
    description:
      'Button in the pending invite tray footer. Cancels the invite on every legal entity the viewer can manage, without a confirmation step.',
  },
});

const NO_INVITES: PendingInviteWithGracePeriod[] = [];

function isSameEmail(a: string | undefined, b: string | undefined) {
  return a != null && b != null && a.toLowerCase() === b.toLowerCase();
}

/**
 * Read-only page for a pending invite (IDFE-4033), at `/team/pending-invite/:invite_id`.
 * Groups the invite with the other pending invites to the same email (one per
 * legal entity) and offers "Cancel invite" for the entities the viewer can manage.
 * Treatment of `t0018_4fba688a` only; control redirects to the Team page.
 */
export function PendingInviteDetail() {
  const { invite_id: inviteId } = useParams<{ invite_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const toast = useToast();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';
  const { isLoaded: areExperimentsLoaded } = useExperiments();
  const isTreatment = useIsTreatment(AppExperiments.t0018_4fba688a);
  const { cancelInvite } = useTeamApi(organizationId);
  const refreshWorkspaceMembers = useRefreshWorkspaceMembers({ organizationId });
  // The invites on screen when "Cancel invite" was clicked. The optimistic update in
  // `cancelInvite` drops them from the cache, so the tray renders this snapshot until
  // the request settles and the cache re-syncs.
  const [cancellingInvites, setCancellingInvites] = useState<PendingInviteWithGracePeriod[]>();
  const isCancelling = cancellingInvites !== undefined;

  const {
    members,
    inviteesByInviteId,
    inviteesByEmail,
    currentMember: currentUser,
    isLoaded: areMembersLoaded,
  } = useWorkspaceTeam(organizationId);

  const invitee = inviteId ? inviteesByInviteId.get(inviteId) : undefined;

  const invite = useMemo<PendingInviteWithGracePeriod | undefined>(
    () => invitee?.invites.find((pending) => pending.inviteId === inviteId),
    [invitee, inviteId],
  );

  // Every invite to the same email, one per legal entity.
  const invites = invitee?.invites ?? NO_INVITES;

  const visibleInvites = cancellingInvites ?? invites;

  // The invite's email outlives the invite, so an open page can follow the person
  // to their member page once they accept.
  const emailRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (invite?.email) {
      emailRef.current = invite.email;
    }
  }, [invite?.email]);

  // IDFE-4022: an admin role still in its grace period doesn't grant manage controls.
  const managePermissionsNow = useManagePermissionsNow(currentUser, organizationId);

  const manageableEntityIds = useMemo(
    () =>
      getManageablePendingInviteEntityIds(
        { pendingInvites: visibleInvites },
        currentUser,
        managePermissionsNow,
      ),
    [currentUser, visibleInvites, managePermissionsNow],
  );

  const handleClose = useCallback(() => {
    logEvent('pending_invite_page_closed', {
      componentType: ComponentType.modal,
      action: ActionType.dismiss,
    });
    navigate(ROUTE_TEAM);
  }, [navigate]);

  const handleCancelInvite = useCallback(async () => {
    if (!invite?.email || manageableEntityIds.length === 0) return;

    setCancellingInvites(invites);
    try {
      await cancelInvite({
        userId: invite.userUuid,
        email: invite.email,
        legalEntityIds: manageableEntityIds,
      });
      logEvent('member_invite_cancelled_success', {
        componentType: ComponentType.api,
        action: ActionType.api,
        source: 'detail_page',
      });
      navigate(ROUTE_TEAM);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logEvent('member_invite_cancelled_failed', {
        componentType: ComponentType.api,
        action: ActionType.api,
        source: 'detail_page',
        error: message,
      });
      toast.show(formatMessage(getCancelInviteErrorMessage(message)));
      // The optimistic update has no rollback. `refreshWorkspaceMembers` joins the
      // re-fetch `cancelInvite` started, so the invite is back in the cache before
      // the not-found redirect below can run.
      await refreshWorkspaceMembers();
    } finally {
      setCancellingInvites(undefined);
    }
  }, [
    cancelInvite,
    formatMessage,
    invite,
    invites,
    manageableEntityIds,
    navigate,
    refreshWorkspaceMembers,
    toast,
  ]);

  useEffect(() => {
    if (isTreatment) {
      logEvent('pending_invite_page_viewed', {
        componentType: ComponentType.page,
        action: ActionType.view,
      });
    }
  }, [isTreatment]);

  // Invite not found: accepted, cancelled elsewhere, or a bad ID. While a cancel is
  // in flight the optimistic update already removed it; that path navigates itself.
  // A partly failed cancel leaves invites to the same email on other entities, so the
  // page moves to one of those and keeps the tray open.
  useEffect(() => {
    if (!areMembersLoaded || invite || isCancelling) return;

    const remainingInvitee = emailRef.current
      ? inviteesByEmail.get(toEmailKey(emailRef.current))
      : undefined;
    const remainingInviteId = remainingInvitee?.invites.find(
      (pending) => pending.inviteId,
    )?.inviteId;
    if (remainingInviteId) {
      navigate(getPendingInviteRoute(remainingInviteId), {
        replace: true,
        state: location.state,
      });
      return;
    }

    const acceptedMember = members.find((member) => isSameEmail(member.email, emailRef.current));
    navigate(acceptedMember?.userUuid ? getTeamMemberRoute(acceptedMember.userUuid) : ROUTE_TEAM, {
      replace: true,
    });
  }, [areMembersLoaded, invite, inviteesByEmail, isCancelling, location.state, members, navigate]);

  const footer = useMemo(
    () =>
      manageableEntityIds.length > 0 ? (
        <PageFooter
          height="auto"
          action={
            <HStack justifyContent="flex-end" alignItems="center">
              <Button
                variant="secondary"
                compact
                onClick={handleCancelInvite}
                loading={isCancelling}
                data-dd-action-name="team_pending_invite_cancel"
                data-testid="team-pending-invite-cancel-button"
              >
                {formatMessage(messages.cancelInvite)}
              </Button>
            </HStack>
          }
        />
      ) : undefined,
    [formatMessage, handleCancelInvite, isCancelling, manageableEntityIds.length],
  );

  if (!organizationId || (areExperimentsLoaded && !isTreatment)) {
    return <Navigate to={ROUTE_TEAM} replace />;
  }

  if (!areExperimentsLoaded || visibleInvites.length === 0) {
    return (
      <Box alignItems="center" justifyContent="center" padding={6}>
        <Spinner size={4} color="fgPrimary" />
      </Box>
    );
  }

  return <PendingInviteTray invites={visibleInvites} onClose={handleClose} footer={footer} />;
}
